import { openDB, type IDBPDatabase } from 'idb';
import { useEffect, useRef, useState } from 'react';

export type DraftBlob = {
  data:      unknown;
  themeCss:  string;
  customCss: string;
  meta:      unknown;
  savedAt:   number;
  /** Informational: the main HEAD sha this draft was last loaded from. */
  loadedFromSha?: string;
};

const DB_NAME = 'nailthelanding-editor';
const STORE   = 'drafts';

const getDb = async (): Promise<IDBPDatabase> =>
  openDB(DB_NAME, 1, {
    upgrade(db) { if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE); },
  });

export const useIndexedDbDraft = (key: string) => {
  const [ready, setReady] = useState(false);
  const [recovered, setRecovered] = useState<DraftBlob | null>(null);
  const dbRef = useRef<IDBPDatabase | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const db = await getDb();
      if (cancelled) return;
      dbRef.current = db;
      const blob = (await db.get(STORE, key)) as DraftBlob | undefined;
      setRecovered(blob ?? null);
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, [key]);

  const save = async (bundle: Omit<DraftBlob, 'savedAt' | 'loadedFromSha'>, loadedFromSha?: string) => {
    const db = dbRef.current;
    if (!db) return;
    await db.put(STORE, { ...bundle, loadedFromSha, savedAt: Date.now() }, key);
  };

  const clear = async () => {
    const db = dbRef.current;
    if (!db) return;
    await db.delete(STORE, key);
    setRecovered(null);
  };

  return { ready, recovered, save, clear };
};
